import React from 'react';

/**
 * Składanie funkcji rozszerzających komponent — zamiennik `compose`
 * z biblioteki `recompose`.
 *
 * DLACZEGO TEN PLIK ISTNIEJE
 * --------------------------
 * `recompose` została **wycofana przez autora**, gdy w Reakcie pojawiły się
 * hooki — miały ją zastąpić w całości. Plan aktualizacji przewidywał na to
 * 6–12 tygodni, zakładając przepisanie komponentów klasowych na funkcyjne.
 *
 * Pomiar na tym kodzie pokazał jednak coś innego: **ze 113 plików
 * importujących `recompose` WSZYSTKIE biorą stamtąd wyłącznie `compose`**.
 * Ani jednego `withState`, `withHandlers`, `lifecycle`, `withProps`, `pure`
 * czy `branch` — czyli ani jednej funkcji, która ma cokolwiek wspólnego
 * z hookami.
 *
 * A `compose` to zwykłe składanie funkcji:
 *
 *     compose(f, g, h)(x)  ===  f(g(h(x)))
 *
 * Nie ma w nim nic reactowego. Wycofana biblioteka zostaje więc w projekcie
 * wyłącznie dla dziesięciu linii kodu.
 *
 * DLACZEGO NIE `compose` Z REDUKSA
 * --------------------------------
 * Redux jest już zależnością i też udostępnia `compose` — ale typuje je
 * inaczej. Wszystkie 113 wywołań w tym projekcie ma postać
 * `compose<TypProps, any>(...)`, czyli z DWOMA parametrami typu, a wersja
 * z Reduksa przyjmuje jeden. Podmiana wymagałaby poprawienia każdego
 * z tych 113 miejsc.
 *
 * Ten plik odtwarza sygnaturę z `@types/recompose` co do znaku, więc
 * zmienia się wyłącznie **linia importu** — żadne wywołanie nie wymaga
 * dotknięcia.
 *
 * CZEGO TO NIE ZAŁATWIA
 * ---------------------
 * Nie przepisuje 118 komponentów klasowych na funkcyjne z hookami. To
 * osobna praca i nadal warta zrobienia — ale teraz jest to **modernizacja
 * z wyboru**, a nie usuwanie wycofanej biblioteki z konieczności.
 */

/** Funkcja rozszerzająca komponent, np. `connect()` albo `withTranslation()`. */
type Rozszerzenie = (komponent: any) => any;

export default function zloz<TWewnetrzne, TZewnetrzne>(
    ...rozszerzenia: Rozszerzenie[]
): (komponent: React.ComponentType<TWewnetrzne>) => React.ComponentType<TZewnetrzne> {
    /*
     * Składamy OD PRAWEJ, tak samo jak `recompose`: ostatnie rozszerzenie
     * opakowuje komponent jako pierwsze. Odwrócenie kolejności zmieniłoby
     * to, który dostaje propsy od którego — a przy `connect()` obok
     * `withTranslation()` byłaby to różnica widoczna dopiero w działaniu.
     */
    return (komponent: any) =>
        rozszerzenia.reduceRight((wynik, rozszerz) => rozszerz(wynik), komponent);
}

export { zloz as compose };
